import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Dialog, EmptyState, ErrorState, Field, LoadingState, Select, TextArea, TextInput } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { api, reviewApi } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { interactionStateFor } from "./interactionStates";
import { InteractionStatePresenter } from "./interactionStatePresenter";
import { memberName, type ReviewMember } from "./memberNames";
import { CONCLUSION_OUTCOME_LABEL, FINDING_STATE_LABEL, LENS_STATE_LABEL, SEVERITY_LABEL, stateLabel, stateTone } from "./labels";

export function ReviewLens() {
  const { reviewId = "", lensId = "" } = useParams();
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const members = useAsync<ReviewMember[]>(async () => (await api.members(orgId)).members, [orgId]);
  const [open, setOpen] = useState(false);
  const [findingOpen, setFindingOpen] = useState(false);
  const [findingAction, setFindingAction] = useState<"raise" | "correct" | "withdraw">("raise");
  const [editingFinding, setEditingFinding] = useState<string | null>(null);
  const [findingTitle, setFindingTitle] = useState("");
  const [findingDescription, setFindingDescription] = useState("");
  const [findingCriterionId, setFindingCriterionId] = useState("");
  const [findingSeverity, setFindingSeverity] = useState("MEDIUM");
  const [findingPage, setFindingPage] = useState("");
  const [findingSection, setFindingSection] = useState("");
  const [findingScopeItemId, setFindingScopeItemId] = useState("");
  const [findingReason, setFindingReason] = useState("");
  const [rationale, setRationale] = useState("");
  const [outcome, setOutcome] = useState("SATISFIED");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [findingError, setFindingError] = useState<unknown>(null);
  const state = useAsync(
    async () => {
      const reviewResponse = await reviewApi.getReview(orgId, reviewId);
      const [findingsResponse, criteriaResponse] = await Promise.all([
        reviewApi.listFindings(orgId, reviewId),
        reviewResponse.review.criteriaSetId
          ? reviewApi.getCriteriaSet(orgId, reviewResponse.review.criteriaSetId)
          : Promise.resolve({ criteriaSet: { criteria: [] } as never }),
      ]);
      return {
        review: reviewResponse.review,
        lens: reviewResponse.review.lenses.find((item) => item.id === lensId),
        findings: findingsResponse.findings.filter((finding) => finding.lensId === lensId),
        criteria: criteriaResponse.criteriaSet.criteria,
      };
    },
    [orgId, reviewId, lensId],
  );
  if (state.loading && !state.data) return <LoadingState label="Loading lens workspace…" />;
  if (state.error) {
    const interactionState = interactionStateFor(state.error);
    return <InteractionStatePresenter state={interactionState} retry={state.reload} />;
  }
  if (!state.data?.lens) return <ErrorState title="Lens not found" />;
  const { review, lens, findings } = state.data;
  const reviewCriteria = state.data.criteria;
  async function conclude() {
    if (!rationale.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      await reviewApi.concludeLens(orgId, reviewId, lensId, { outcome, rationale: rationale.trim() });
      setOpen(false);
      setRationale("");
      state.reload();
    } catch (error) {
      setActionError(error);
    } finally {
      setSaving(false);
    }
  }
  function openFinding(action: "raise" | "correct" | "withdraw", finding?: typeof findings[number]) {
    setFindingAction(action);
    setEditingFinding(finding?.id ?? null);
    setFindingTitle(finding?.title ?? "");
    setFindingDescription(finding?.description ?? "");
    setFindingCriterionId(finding?.criterionId ?? state.data?.criteria[0]?.id ?? "");
    setFindingSeverity(finding?.severity ?? "MEDIUM");
    setFindingPage(finding?.location?.page?.toString() ?? "");
    setFindingSection(finding?.location?.section ?? "");
    const matchingScope = finding?.location
      ? review.scopeItems.find((scope) => scope.documentId === finding.location?.documentId && scope.version === finding.location?.version)
      : undefined;
    setFindingScopeItemId(matchingScope?.id ?? (review.scopeItems.length === 1 ? review.scopeItems[0]!.id : ""));
    setFindingReason("");
    setFindingError(null);
    setFindingOpen(true);
  }
  async function saveFinding() {
    setFindingError(null);
    const selectedScope = review.scopeItems.find((scope) => scope.id === findingScopeItemId);
    if (!selectedScope) {
      setFindingError(new Error("Select the pinned document version for this finding."));
      return;
    }
    try {
      if (findingAction === "raise") {
        await reviewApi.raiseFinding(orgId, reviewId, {
          lensId,
          criterionId: findingCriterionId,
          severity: findingSeverity,
          summary: findingTitle.trim(),
          detail: findingDescription.trim() || null,
          anchors: [{
            scopeItemId: selectedScope.id,
            anchorKind: findingPage ? "PAGE" : "SECTION",
            ...(findingPage ? { pageNumber: Number(findingPage) } : { sectionPath: findingSection || "Unspecified" }),
          }],
        });
      } else if (findingAction === "correct" && editingFinding) {
        await reviewApi.correctFinding(orgId, reviewId, editingFinding, {
          summary: findingTitle.trim(),
          detail: findingDescription.trim() || null,
        });
      } else if (editingFinding) {
        await reviewApi.withdrawFinding(orgId, reviewId, editingFinding, findingReason.trim());
      }
      setFindingOpen(false);
      state.reload();
    } catch (error) {
      setFindingError(error);
    }
  }
  const disabledByPermission = actionError instanceof ApiError && actionError.code === "forbidden" && typeof actionError.details?.permission === "string";
  const permission = disabledByPermission ? String((actionError as ApiError).details?.permission) : null;
  return (
    <>
      <div className="page-head">
        <div>
          <p><Link to={`/studio/review-ai/reviews/${reviewId}`}>{review.purpose}</Link> / Lens workspace</p>
          <h1>{lens.lensKey}</h1>
        <p>Assigned reviewer: {memberName(members.data ?? [], lens.assignedReviewerId)}</p>
        </div>
        <Badge tone={stateTone(lens.state)}>{stateLabel(lens.state, LENS_STATE_LABEL)}</Badge>
      </div>
      <h2 className="ods-visually-hidden">Lens details</h2>
      <Card>
        <CardHeader title="Review against criteria" />
        <CardBody>
          <p>Criteria set {review.criteriaSetId}, version {review.criteriaSetVersion}. Findings are anchored to the pinned version only.</p>
          <Button onClick={() => setOpen(true)} disabled={lens.state === "CONCLUDED" || disabledByPermission}>
            Conclude lens
          </Button>
          {permission ? <p role="status">You do not hold {permission}; this control is disabled.</p> : null}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Findings" />
        <CardBody>
          <Button size="sm" onClick={() => openFinding("raise")}>Raise finding</Button>
          {findings.map((finding) => (
            <div key={finding.id} className="ods-card" style={{ marginBottom: "var(--ods-space-2)" }}>
              <strong>{finding.title}</strong>
              <p>{finding.description}</p>
              <div className="ods-row">
                <Badge tone={stateTone(finding.severity)}>{SEVERITY_LABEL[finding.severity] ?? finding.severity}</Badge>
                <Badge tone={stateTone(finding.state)}>{stateLabel(finding.state, FINDING_STATE_LABEL)}</Badge>
                <span>
                  {finding.location?.documentId}, version {finding.location?.version}, checksum {finding.location?.checksum}
                  {finding.location?.page ? `, page ${finding.location.page}` : ""}
                </span>
              </div>
              <div className="ods-row">
                <Button size="sm" variant="secondary" onClick={() => openFinding("correct", finding)}>Correct</Button>
                <Button size="sm" variant="secondary" onClick={() => openFinding("withdraw", finding)}>Withdraw</Button>
              </div>
            </div>
          ))}
          {findings.length === 0 ? <EmptyState title="No findings" description="No findings have been raised on this lens." /> : null}
        </CardBody>
      </Card>
      <Dialog
        open={findingOpen}
        title={findingAction === "raise" ? "Raise finding" : findingAction === "correct" ? "Correct finding" : "Withdraw finding"}
        description={`You are acting as ${user?.displayName ?? user?.email ?? "the current user"}. Review AI records this operational action.`}
        onClose={() => setFindingOpen(false)}
        footer={<>
          <Button variant="secondary" onClick={() => setFindingOpen(false)}>Cancel</Button>
          <Button onClick={saveFinding} disabled={findingAction === "withdraw" ? !findingReason.trim() : !findingTitle.trim() || (!findingDescription.trim() && findingAction === "raise") || !findingCriterionId || (!findingPage.trim() && !findingSection.trim())}>
            {findingAction === "raise" ? "Raise finding" : findingAction === "correct" ? "Save correction" : "Withdraw finding"}
          </Button>
        </>}
      >
        {findingAction === "withdraw" ? (
          <Field label="Reason">{(id) => <TextArea id={id} rows={3} value={findingReason} onChange={(event) => setFindingReason(event.target.value)} />}</Field>
        ) : (
          <>
            <Field label="Title">{(id) => <TextInput id={id} value={findingTitle} onChange={(event) => setFindingTitle(event.target.value)} />}</Field>
            <Field label="Description">{(id) => <TextArea id={id} rows={3} value={findingDescription} onChange={(event) => setFindingDescription(event.target.value)} />}</Field>
            {state.data.criteria.length > 0 ? (
              <Field label="Criterion">{(id) => (
                <Select id={id} value={findingCriterionId} onChange={(event) => setFindingCriterionId(event.target.value)}>
                  <option value="">Select a criterion</option>
                  {reviewCriteria.map((criterion) => <option key={criterion.id} value={criterion.id}>{criterion.code} — {criterion.criterionText}</option>)}
                </Select>
              )}</Field>
            ) : null}
            <Field label="Severity">{(id) => (
              <Select id={id} value={findingSeverity} onChange={(event) => setFindingSeverity(event.target.value)}>
                {Object.entries(SEVERITY_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </Select>
            )}</Field>
            {review.scopeItems.length > 1 ? (
              <Field label="Pinned document version" help="Choose the immutable scope item that anchors this finding.">
                {(id) => (
                  <Select id={id} value={findingScopeItemId} onChange={(event) => setFindingScopeItemId(event.target.value)}>
                    <option value="">Select a pinned document version</option>
                    {review.scopeItems.map((scope) => <option key={scope.id} value={scope.id}>{scope.documentId} · version {scope.version} · {scope.checksum}</option>)}
                  </Select>
                )}
              </Field>
            ) : null}
            <Field label="Page coordinate">{(id) => <TextInput id={id} inputMode="numeric" value={findingPage} onChange={(event) => setFindingPage(event.target.value)} />}</Field>
            <Field label="Section coordinate">{(id) => <TextInput id={id} value={findingSection} onChange={(event) => setFindingSection(event.target.value)} />}</Field>
          </>
        )}
        {findingError ? <p role="alert">Finding action failed: {findingError instanceof Error ? findingError.message : "Review AI refused the action."}</p> : null}
      </Dialog>
      <Dialog
        open={open}
        title="Conclude lens"
        description={`You are acting as ${user?.displayName ?? user?.email ?? "the current user"}. Review AI will freeze this lens after conclusion.`}
        onClose={() => setOpen(false)}
        dismissible={!saving}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={conclude} disabled={saving || !rationale.trim() || disabledByPermission}>{saving ? "Concluding…" : "Conclude lens"}</Button>
          </>
        }
      >
        <Field label="Conclusion">{(id) => (
          <Select id={id} value={outcome} onChange={(event) => setOutcome(event.target.value)}>
            {Object.entries(CONCLUSION_OUTCOME_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </Select>
        )}</Field>
        <Field label="Rationale" help="A rationale is required before a lens can be concluded.">
          {(id) => <TextArea id={id} rows={4} value={rationale} onChange={(event) => setRationale(event.target.value)} />}
        </Field>
        {actionError ? (
          <InteractionStatePresenter
            state={interactionStateFor(actionError)}
            alternativeActor={memberName(members.data ?? [], lens.assignedReviewerId)}
            {...(permission ? { permission } : {})}
          />
        ) : null}
      </Dialog>
    </>
  );
}
